// catalogoLocal.js — catálogo de productos cacheado en el dispositivo
// (IndexedDB) para que el buscador de material del técnico funcione sin
// esperar a Firestore y sin conexión (Prioridad 1.3).
//
// El servidor genera de madrugada un archivo compacto (generarCatalogoProductos.js,
// formato tupla [referencia, nombre, ean, precio] — sin repetir claves, con
// ~22.700 productos eso es la diferencia entre unos pocos MB y el doble).
// Esta app lo descarga solo cuando getCatalogoVersion.js dice que cambió la
// versión, lo guarda entero en IndexedDB, y busca en memoria (sin red) —
// normaliza acentos/mayúsculas y tolera una pequeña errata (distancia de
// edición 1) si la búsqueda exacta no encuentra nada.
import { authService } from '../services/authService';

const DB_NAME    = 'zapata_catalogo';
const DB_VERSION = 1;
const STORE      = 'catalogo';
const KEY        = 'productos';

let memoria = null; // array de tuplas, cacheado en RAM tras la primera carga

// Bug real encontrado en producción (Rafa, 2026-10-16): un dispositivo tenía
// la base `zapata_catalogo` ya creada SIN el almacén `catalogo` — como
// `onupgradeneeded` solo se dispara si DB_VERSION sube, abrir con la MISMA
// versión de siempre nunca lo repara, y cada lectura/escritura fallaba para
// siempre con "One of the specified object stores was not found" — en
// silencio (los call-sites de idbGet() ya hacían .catch(() => null)), así
// que el buscador de material se quedaba sin catálogo sin ningún aviso. Se
// detecta ese caso y se borra+recrea la base limpia en vez de fallar para
// siempre.
function abrirDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => { req.result.createObjectStore(STORE); };
    req.onsuccess = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.close();
        const delReq = indexedDB.deleteDatabase(DB_NAME);
        // deleteDatabase() se queda colgado para siempre (ni onsuccess ni
        // onerror) si hay OTRA conexión abierta a la misma base sin cerrar
        // (otra pestaña, o una conexión anterior que nunca se cerró) — solo
        // dispara onblocked. Sin esto, un técnico con dos pestañas abiertas
        // se quedaba con "Cargando catálogo…" para siempre en vez de,
        // simplemente, no poder reparar la caché esta vez.
        delReq.onblocked = () => reject(new Error('IndexedDB bloqueada por otra pestaña — ciérrala e inténtalo de nuevo'));
        delReq.onsuccess = () => resolve(abrirDB());
        delReq.onerror   = () => reject(delReq.error);
        return;
      }
      resolve(db);
    };
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(key) {
  const db = await abrirDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

async function idbSet(key, value) {
  const db = await abrirDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror    = () => reject(tx.error);
  });
}

// Descarga el archivo completo solo si la versión guardada en el dispositivo
// no coincide con la del servidor. Devuelve { actualizado, count, error }.
export async function asegurarCatalogoActualizado() {
  try {
    const resp = await fetch('/.netlify/functions/getCatalogoVersion', { headers: { ...authService.authHeader() } });
    const data = await resp.json();
    if (!data.success || !data.version) return { actualizado: false, error: data.error || null };

    const local = await idbGet(KEY).catch(() => null);
    if (local && local.version === data.version) {
      memoria = local.filas;
      return { actualizado: true, count: local.filas.length, descargado: false };
    }

    // No se usa data.url (la URL pública de Storage con downloadTokens) — el
    // bucket nunca tuvo CORS configurado para el dominio de la app, así que
    // ese fetch directo siempre fallaba en silencio ("blocked by CORS
    // policy", encontrado probando esto mismo en real, 2026-10-16). Se pide
    // a través de nuestra propia función (getCatalogoArchivo.js), que lo lee
    // server-to-server con la cuenta de servicio — mismo origen, sin CORS.
    const filasResp = await fetch('/.netlify/functions/getCatalogoArchivo', { headers: { ...authService.authHeader() } });
    if (!filasResp.ok) throw new Error(`No se pudo descargar el catálogo (${filasResp.status})`);
    const filas = await filasResp.json();
    // `memoria` se fija ANTES de intentar persistir — si IndexedDB falla (el
    // bug de arriba, cuota llena, modo privado...) el técnico sigue teniendo
    // el catálogo recién descargado para esta sesión; solo se pierde la
    // caché para la próxima vez, no la búsqueda de ahora mismo.
    memoria = filas;
    await idbSet(KEY, { version: data.version, filas }).catch(() => {});
    return { actualizado: true, count: filas.length, descargado: true };
  } catch (e) {
    // Sin red o sin catálogo todavía: si ya había uno guardado de antes, se
    // sigue usando ese en vez de dejar al técnico sin buscador.
    const local = await idbGet(KEY).catch(() => null);
    if (local) { memoria = local.filas; return { actualizado: true, count: local.filas.length, descargado: false }; }
    return { actualizado: false, error: e.message };
  }
}

export function catalogoListo() {
  return Array.isArray(memoria);
}

function normalizar(s) {
  return String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

// Distancia de edición acotada (Damerau-Levenshtein restringida: incluye
// transposición de letras adyacentes como una sola edición, el tipo de
// errata más común al escribir rápido en el móvil — "flitro" por "filtro").
// Solo se usa como último recurso (ver abajo) sobre un universo ya filtrado
// por longitud similar, así que nunca compara contra las ~22.700 filas con
// el algoritmo completo.
function distanciaEdicion(a, b, limite) {
  if (Math.abs(a.length - b.length) > limite) return limite + 1;
  const dp = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 0; j <= b.length; j++) dp[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]);
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
          dp[i][j] = Math.min(dp[i][j], dp[i - 2][j - 2] + 1);
        }
      }
    }
  }
  return dp[a.length][b.length];
}

// id = ID del documento en `productos` (necesario como productoId para que
// finalizarVisita.js descuente stock — ver generarCatalogoProductos.js).
function filaAProducto([id, referencia, nombre, ean, precio]) {
  return { id, referencia, nombre, ean, precioVenta: precio };
}

// Pura (recibe las filas explícitamente) — así se puede probar sin montar
// IndexedDB/fetch, mismo criterio que validateOrders()/getBaseOutcomes() en
// la vista de técnico. `termino` de al menos 2 caracteres.
export function buscarEnFilas(filas, termino, { max = 8 } = {}) {
  if (!Array.isArray(filas)) return [];
  const t = normalizar(termino || '');
  if (t.length < 2) return [];

  const exactos = [];
  for (const fila of filas) {
    const [, referencia, nombre, ean] = fila;
    if (ean && ean === termino.trim()) { exactos.unshift(fila); continue; } // EAN exacto primero
    if (normalizar(nombre).includes(t) || normalizar(referencia).includes(t)) exactos.push(fila);
    if (exactos.length >= max * 4) break; // suficientes candidatos, no hace falta recorrer todo
  }
  if (exactos.length > 0) return exactos.slice(0, max).map(filaAProducto);

  // Tolerante a erratas: solo si no hubo ningún resultado exacto y el término
  // es lo bastante largo como para que una distancia de 1 siga siendo útil.
  if (t.length < 4) return [];
  const candidatos = [];
  for (const fila of filas) {
    const nombreNorm = normalizar(fila[2]);
    const palabras = nombreNorm.split(/\s+/);
    if (palabras.some(p => distanciaEdicion(p, t, 1) <= 1)) candidatos.push(fila);
    if (candidatos.length >= max) break;
  }
  return candidatos.slice(0, max).map(filaAProducto);
}

// Busca en memoria — sin red. `termino` de al menos 2 caracteres.
export function buscarEnCatalogoLocal(termino, opts) {
  return buscarEnFilas(memoria, termino, opts);
}

// Productos más usados por este técnico (ver getProductosFrecuentesTecnico.js)
// — se muestran cuando el técnico abre el buscador sin haber escrito nada
// todavía. Cacheado en localStorage un día; no hace falta pedirlo en cada
// apertura del modal.
const CACHE_FRECUENTES_MS = 24 * 60 * 60 * 1000;

export async function obtenerProductosFrecuentes(employeeId) {
  if (!employeeId) return [];
  const cacheKey = `zapata_frecuentes_${employeeId}`;
  try {
    const cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
    if (cached && Date.now() - cached.ts < CACHE_FRECUENTES_MS) return cached.productos;
  } catch { /* localStorage puede fallar en privado — se ignora, se vuelve a pedir */ }

  try {
    const resp = await fetch(`/.netlify/functions/getProductosFrecuentesTecnico?employeeId=${employeeId}`, { headers: { ...authService.authHeader() } });
    const data = await resp.json();
    if (!data.success) return [];
    try { localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), productos: data.productos })); } catch { /* best-effort */ }
    return data.productos;
  } catch {
    return [];
  }
}

// Resuelve cada referencia "frecuente" (solo tiene referencia/nombre/count,
// viene del histórico de líneas, no del catálogo) contra el catálogo local
// para tener el producto completo (precio, ean) y poder añadirlo igual que
// un resultado de búsqueda normal.
export function resolverFrecuentesEnFilas(filas, frecuentes) {
  if (!Array.isArray(filas) || !frecuentes?.length) return [];
  const porReferencia = new Map(filas.map(f => [f[1], f]));
  return frecuentes
    .map(f => porReferencia.get(f.referencia))
    .filter(Boolean)
    .map(filaAProducto);
}

export function resolverFrecuentesEnCatalogo(frecuentes) {
  return resolverFrecuentesEnFilas(memoria, frecuentes);
}

// Reordena resultados de búsqueda poniendo primero los que ya se usaron en
// este equipo o en este modelo (catálogo de piezas, item 3) — usado por
// LineBuilder y ProductoSelector en FinalizarVisitaModal.js. 'equipo' tiene
// prioridad sobre 'modelo' si una pieza estuviera en los dos. Pura: no toca
// `memoria`, fácil de probar con listas sueltas.
export function ordenarConSugerencias(resultados, sugerenciasEquipo = [], sugerenciasModelo = []) {
  const equipoSet  = new Set(sugerenciasEquipo.map(p => p.referencia));
  const modeloSet  = new Set(sugerenciasModelo.map(p => p.referencia));
  const destacados = [];
  const resto      = [];
  for (const p of resultados) {
    if (equipoSet.has(p.referencia)) destacados.push({ ...p, nivel: 'equipo' });
    else if (modeloSet.has(p.referencia)) destacados.push({ ...p, nivel: 'modelo' });
    else resto.push(p);
  }
  return [...destacados, ...resto];
}
